<template>
  <v-container class="grey-lighten-3 full-height d-flex flex-column">
    <!-- Loading State -->
    <v-row v-if="loading" justify="center">
      <v-col cols="12" class="text-center">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
        <p class="mt-2">Loading LC details...</p>
      </v-col>
    </v-row>

    <!-- Error State -->
    <v-row v-else-if="!lcData || Object.keys(lcData).length === 0" justify="center">
      <v-col cols="12" class="text-center">
        <p class="text-grey">No LC data found</p>
        <v-btn color="primary" @click="goBack" class="mt-4">Go Back</v-btn>
      </v-col>
    </v-row>

    <!-- Printable Area -->
    <v-row v-else justify="center">
      <v-col cols="12" xl="10" lg="11" md="12">
        <div class="print-container" id="printable-area">
          <!-- Header Section -->
          <div class="header">
            <img
              v-if="getCurrentCompany?.logo_thumb"
              :src="`${getFileServerUrl.replace(/\/$/, '')}/${getCurrentCompany.logo_thumb}`"
              alt="Company Logo"
              class="company-logo"
            />
            <div v-else class="logo-placeholder">
              <v-icon icon="mdi-domain" size="60" color="grey-darken-2"></v-icon>
            </div>
            <div class="company-info">
              <strong>{{ getCurrentCompany?.name || 'Company Name' }}</strong><br>
              {{ getCurrentCompany?.address || 'Company Address' }}<br>
              <span v-if="getCurrentCompany?.email">Email: {{ getCurrentCompany.email }}</span>
              <span v-if="getCurrentCompany?.mobile"> | Phone: {{ getCurrentCompany.mobile }}</span>
            </div>
          </div>

          <!-- LC Title -->
          <div class="lc-header-info">
            <h2 class="lc-title">COMMERCIAL INVOICE (LC)</h2>
            <p class="lc-meta">
              <strong>Invoice No:</strong> {{ lcData.invoice_no || 'N/A' }} | 
              <strong>Date:</strong> {{ formatDate(lcData.invoice_date) }}
            </p>
          </div>

          <!-- Customer & LC Info -->
          <div class="section-title">Invoice Information</div>
          <div class="info-section">
            <table class="info-table">
              <tbody>
                <tr>
                    <td  class="info-label"><strong>LC Code</strong></td>
                    <td  class="info-label" colspan="3">{{ lcData?.lc_master?.code }}</td>
                </tr>
                <tr>
                  <td class="info-label"><strong>Customer:</strong></td>
                  <td class="info-value">{{ lcData.customer?.name || 'N/A' }}</td>
                  <td class="info-label"><strong>Currency:</strong></td>
                  <td class="info-value">{{ lcData.currency?.code || lcData.currency?.name || 'USD' }}</td>
                </tr>
                <tr>
                  <td class="info-label"><strong>Code:</strong></td>
                  <td class="info-value">{{ lcData.code || 'N/A' }}</td>
                  <td class="info-label"><strong>Status:</strong></td>
                  <td class="info-value">{{ lcData.approval_status || 'Pending' }}</td>
                </tr>
                <tr>
                  <td class="info-label"><strong>Total Amount:</strong></td>
                  <td class="info-value">{{ formatCurrency(lcData.total_amount) }}</td>
                  <td class="info-label"><strong>Remarks:</strong></td>
                  <td class="info-value">{{ lcData.remarks || '-' }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Invoice Items -->
          <div class="section-title">Invoice Details</div>
          <div class="pi-items-section">
            <table class="items-table">
              <thead>
                <tr>
                  <th>SL</th>
                  <th>Product Code</th>
                  <th>Description</th>
                  <th>Qty</th>
                  <th>Unit Price</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in lcData.details" :key="item.id">
                  <td>{{ index + 1 }}</td>
                  <td>{{ item.product?.code || 'N/A' }}</td>
                  <td>{{ item.product?.name || 'N/A' }}</td>
                  <td class="text-right">{{ formatNumber(item.qty) }}</td>
                  <td class="text-right">{{ formatCurrency(item.unit_price, lcData.currency?.code) }}</td>
                  <td class="text-right">{{ formatCurrency(item.total, lcData.currency?.code) }}</td>
                </tr>
                <tr v-if="!lcData.details || lcData.details.length === 0">
                  <td colspan="6" class="text-center text-grey">No items found</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- LC Summary -->
          <div class="lc-summary-section">
            <div class="section-title">Summary</div>
            <table class="summary-table">
              <tbody>
                <tr>
                  <td><strong>Total Amount:</strong></td>
                  <td class="text-right">{{ formatCurrency(lcData.total_amount) }}</td>
                </tr>
                <tr>
                  <td><strong>Number of Items:</strong></td>
                  <td class="text-right">{{ lcData.details?.length || 0 }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Signatures -->
          <div class="signature-area">
            <div class="signature-box">
              <div class="signature-line"></div>
              <p class="text-center mt-2">Prepared By</p>
            </div>
            <div class="signature-box">
              <div class="signature-line"></div>
              <p class="text-center mt-2">Checked By</p>
            </div>
            <div class="signature-box">
              <div class="signature-line"></div>
              <p class="text-center mt-2">Approved By</p>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Action Toolbar -->
    <v-row class="d-print-none mb-4" justify="center" v-if="!loading && lcData">
      <v-col cols="12" class="text-center">
        <v-btn color="primary" class="mr-2" @click="printPage">
          <v-icon left>mdi-printer</v-icon> Print
        </v-btn>
        <v-btn color="success" @click="downloadPDF">
          <v-icon left>mdi-file-pdf-box</v-icon> Download PDF
        </v-btn>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";
import html2pdf from "html2pdf.js";
import { formatDateView, formatDateDash } from "@/plugins/helpers";

export default {
  name: "LcInvoiceView",
  setup() {
    const router = useRouter();
    const route = useRoute();
    const store = useStore();

    const loading = ref(false);
    const lcData = ref({});

    const formatDate = (date) => {
      if (!date) return "-";
      return formatDateView(date);
    };

    const formatNumber = (num) => {
      if (!num && num !== 0) return "0.00";
      return Number(num).toFixed(2);
    };

    const formatCurrency = (amount, currencyCode = null) => {
      if (!amount && amount !== 0) return "0.00";
      const formatted = Number(amount).toFixed(2);
      const currency = currencyCode || lcData.value.currency?.code || lcData.value.currency?.name || 'USD';
      return `${currency} ${formatted}`;
    };

    const loadLcData = async (id) => {
      if (!id) {
        toast.error("No LC ID provided");
        router.push("/lc/import-lc-record");
        return;
      }
      loading.value = true;
      try {
        const data = await store.dispatch("getExportLcCommercialInvoiceById", id);
        lcData.value = data;
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to load LC data");
        router.push("/lc/import-lc-record");
      } finally {
        loading.value = false;
      }
    };

    const goBack = () => {
      router.push("/lc/import-lc-record");
    };

    const downloadPDF = async () => {
      const element = document.getElementById("printable-area");
      const opt = {
        margin: 0.5,
        filename: `lc-invoice-${lcData.value.code || lcData.value.invoice_no}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "cm", format: "a4", orientation: "portrait" },
      };
      await html2pdf().set(opt).from(element).save();
    };

    const printPage = () => {
        const element = document.getElementById("printable-area").innerHTML;
        const printWindow = window.open("", "_blank", "width=900,height=600");
        printWindow.document.write(`
            <html>
                <head>
                    <title>Invoice</title>
                    <style>
                        body,html{margin:0;padding:0;background:#fff !important;font-family:Arial,Helvetica,sans-serif;color:#000;}
                        .full-height{min-height:auto;display:block;}
                        .print-container{width:100%;max-width:800px;margin:auto;padding:20px;box-sizing:border-box;background:#fff;}
                        .header{display:flex;align-items:center;border-bottom:2px solid #333;padding-bottom:10px;margin-bottom:15px;}
                        .company-logo{max-width:120px;max-height:60px;margin-right:20px;}
                        .logo-placeholder{width:120px;height:60px;display:flex;align-items:center;justify-content:center;border:1px solid #999;margin-right:20px;}
                        .company-info{font-size:12px;line-height:1.2;}
                        .lc-header-info{text-align:center;margin-bottom:15px;}
                        .lc-title{font-size:18px;margin:0;font-weight:bold;}
                        .lc-meta{font-size:12px;margin:4px 0 0;}
                        .section-title{background:#f2f2f2 !important;padding:6px 8px;margin:20px 0 8px;font-weight:bold;border-left:4px solid #555;font-size:13px;}
                        table{width:100%;border-collapse:collapse;font-size:12px;}
                        table td,table th{border:1px solid #999;padding:4px 6px;}
                        .text-right{text-align:right !important;}
                        .text-center{text-align:center !important;}
                        .signature-area{display:flex;justify-content:space-between;margin-top:50px;}
                        .signature-box{width:220px;text-align:center;}
                        .signature-line{border-top:1px solid #000;margin-top:40px;}
                        .d-print-none{display:none !important;}
                        .pi-items-section{margin-top:10px;}
                        .info-section{margin-top:10px;}
                        tr{page-break-inside:avoid;}
                    </style>
                </head>
                <body>${element}</body>
             </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
    };

    onMounted(() => {
      const id = route.params.id;
      if (id) loadLcData(id);
      else toast.error("No LC ID provided");
    });

    return {
      loading,
      lcData,
      formatDate,
      formatNumber,
      formatCurrency,
      goBack,
      downloadPDF,
      printPage,
      getCurrentCompany: computed(() => store.getters.getCurrentCompany),
      getFileServerUrl: computed(() => store.getters.getFileServerUrl),
    };
  },
};
</script>

<style scoped>
.full-height {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.print-container {
  width: 800px;
  margin: auto;
  padding: 25px;
  background: white;
}

.header {
  text-align: center;
  border-bottom: 2px solid #333;
  padding-bottom: 10px;
  margin-bottom: 15px;
}

.company-info {
  font-size: 14px;
  margin-top: 5px;
}

.section-title {
  background: #f2f2f2;
  padding: 8px;
  margin-top: 20px;
  font-weight: bold;
  border-left: 4px solid #555;
}

.info-table,
.items-table,
.summary-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.info-table td,
.items-table td,
.items-table th,
.summary-table td {
  border: 1px solid #999;
  padding: 6px 8px;
}

.text-right {
  text-align: right;
}

.text-center {
  text-align: center;
}

.signature-area {
  display: flex;
  justify-content: space-between;
  margin-top: 50px;
}

.signature-box {
  width: 220px;
  text-align: center;
}

.signature-line {
  border-top: 1px solid #000;
  margin-top: 40px;
}
</style>
